import {
  ApiProjectSetting,
  ApiProjectSettingKey,
  ApiProjectType,
} from "@incendium/api";
import {
  Autocomplete,
  Button,
  Divider,
  Box,
  Stack,
} from "@mui/material";
import { updateProject } from "Actions";
import { IOnboardStepProps } from "features/onboarding/types";
import { saveProject } from "features/project";
import { enumToArray, formatEnumVal } from "Helpers/enumToText";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch } from "react-redux";
import { saveProjectSetting } from "features/project";
import produce from "immer";
import { useNotification } from "Hooks";

interface IOnboardingStepBusinessInfoProps extends IOnboardStepProps {
  projectSetting: ApiProjectSetting;
  setProjectSettings: Dispatch<SetStateAction<ApiProjectSetting[]>>;
}

function OnboardingStepBusinessInfo({
  project,
  projectSetting,
  setProjectSettings,
}: IOnboardingStepBusinessInfoProps) {
  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [description, setDescription] = useState(project?.overview || "");
  const [category, setCategory] = useState<string[]>(
    projectSetting.value
      ? (projectSetting.value as string)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      : []
  );

  useEffect(() => {
    if (!projectSetting.value) {
      return;
    }
    setCategory(
      (projectSetting.value as string)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }, [projectSetting]);

  // Track if form has changed from original values
  const dirty =
    description !== (project?.overview || "") ||
    category.join(",") !== (projectSetting.value || "");

  const onSubmit = useCallback(async () => {
    try {
      if (description !== project?.overview) {
        const res = await saveProject(project?.clientId as number, {
          ...project,
          overview: description,
        });

        dispatch(updateProject(res));
      }

      if (!projectSetting.value || category.join(",") !== projectSetting.value) {
        await saveProjectSetting(
          {
            key: ApiProjectSettingKey.PROJECT_TYPE,
            value: category.join(","),
          },
          project!
        );
        setProjectSettings((settings) =>
          produce(settings, (draft) => {
            const idx = draft.findIndex(
              (d) => d.key === ApiProjectSettingKey.PROJECT_TYPE
            );
            if (idx >= 0) {
              draft[idx].value = category.join(",");
            }
          })
        );
      }
      showSuccessNotification("Information Saved");
    } catch (e) {
      showErrorNotification("There was an error saving this information, please try again");
    }

  }, [
    description,
    category,
    project,
    dispatch,
    projectSetting,
    setProjectSettings,
    showSuccessNotification,
    showErrorNotification,
  ]);

  return (
    <ValidatorForm onSubmit={onSubmit} style={{ height: "100%" }}>
      <Stack justifyContent="space-between" height="100%">
        <Stack mb={3} spacing={3}>
            <Autocomplete
              multiple
              options={enumToArray(ApiProjectType).filter(
                (s) => s !== ApiProjectType.TYPE_UNSPECIFIED
              )}
              getOptionLabel={(o) => formatEnumVal(o.replace("Type", ""))}
              value={category as any}
              onChange={(e, v) => {
                setCategory((v || []) as string[]);
              }}
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  label="Business Type"
                  name="category"
                  size="medium"
                  value={category.join(",")}
                  validators={["required"]}
                  errorMessages={["Your Business type is Required"]}
                />
              )}
            />
            <TextValidator
              label="Brand Description"
              fullWidth
              multiline
              rows={5}
              rowsMax={8}
              variant="outlined"
              value={description}
              name="description"
              validators={["required", "maxStringLength:512"]}
              errorMessages={[
                "Your Business Description is Required",
                "Business description can only be upto 512 characters long",
              ]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDescription(e.target.value);
              }}
            />
        </Stack>
        <Box>
          <Divider />
          <Stack direction="row" spacing={2} mt={2} justifyContent="flex-end">
            <Button
              fullWidth
              color="primary"
              size="large"
              type="submit"
              disabled={!dirty}
            >
              Save Changes
            </Button>
          </Stack>
        </Box>
      </Stack>
    </ValidatorForm>
  );
}

export default OnboardingStepBusinessInfo;
